import { useCallback, useState } from 'react'
import { DataTablePFSEvent, DataTableSortOrderType } from 'primereact/datatable'

// Root cause this solves: the Costs (and Áreas) tables run the shared Table in lazy mode
// (lazySettings present -> PrimeReact lazy=true), so PrimeReact never reorders the rows itself and
// delegates to the parent's onSort. Every page wired an empty onSort handler, so clicking a sort icon
// only toggled the header indicator but left the data untouched. This hook fills that gap by sorting the
// already-loaded page rows on the client, by the column's raw value (not its formatted/rendered text).

// Compares two raw cell values by their natural type: numbers numerically, booleans false<true, and
// everything else (including ISO date strings, which are lexicographically chronological) with a
// locale-aware, case-insensitive, numeric-aware string compare. Nulls/undefined sort last (ascending).
const compareValues = (a: unknown, b: unknown): number => {
	if (a == null && b == null) return 0
	if (a == null) return 1
	if (b == null) return -1
	if (typeof a === 'number' && typeof b === 'number') return a - b
	if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1
	return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

// Client-side sort for the shared lazy Table: tracks the active sort column/direction and exposes a
// pure `sortData` that returns a reordered copy of the current page's rows. Sorting the raw field value
// (e.g. the ISO `lastModifiedAt`, not the "dd/mm/yyyy hh:mm" shown by the body template) keeps dates,
// numbers and text ordered correctly regardless of any visual formatting.
export const useTableSort = <T extends object>() => {
	const [sortField, setSortField] = useState<string | undefined>(undefined)
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>(undefined)

	const handleSort = useCallback((event: DataTablePFSEvent) => {
		setSortField(event.sortField)
		setSortOrder(event.sortOrder)
	}, [])

	const sortData = useCallback(
		(rows: T[]): T[] => {
			if (!sortField || !sortOrder) return rows
			return [...rows].sort(
				(r1, r2) =>
					sortOrder *
					compareValues((r1 as Record<string, unknown>)[sortField], (r2 as Record<string, unknown>)[sortField])
			)
		},
		[sortField, sortOrder]
	)

	return { sortField, sortOrder, handleSort, sortData }
}
